import { type FC, useMemo, type SyntheticEvent, Suspense, lazy, type ComponentProps } from "react";
import { retryLazyLoad } from "@root/shared/utils/retryLazyLoad";
import type { Pie as PieType } from "react-chartjs-2";
import { type ColorSetting, type Name, backgroundColors, borderColor } from "./colors";

const PieComponent = lazy(() =>
  retryLazyLoad<ComponentProps<typeof PieType>>({
    fn: () => import("react-chartjs-2").then(({ Pie }) => ({ default: Pie })),
  }),
);

type Props = {
  isSP: boolean;
  items: {
    label: string;
    value: number;
    name: Name;
  }[];
  colorSetting?: ColorSetting;
};

type TooltipItemType = {
  index: number;
};

type DataType = {
  labels: number[];
  datasets: string[];
};

export const PieChart: FC<Props> = ({ isSP, items, colorSetting = "default" }) => {
  const data = useMemo(
    () => ({
      labels: items.map((item) => item.label),
      datasets: [
        {
          data: items.map((item) => item.value),
          backgroundColor: items.map((item) => backgroundColors[colorSetting][item.name]),
          borderColor: borderColor[colorSetting],
        },
      ],
    }),
    [items, colorSetting],
  );

  const options = useMemo(
    () => ({
      animation: {
        duration: 0,
      },
      hover: {
        mode: null,
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: false,
            },
            ticks: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              display: false,
              tickMarkLength: isSP ? -14 : 0,
            },
            ticks: {
              display: false,
            },
          },
        ],
      },
      legend: {
        onClick: (e: SyntheticEvent) => e.stopPropagation(),
        position: isSP ? "right" : "bottom",
        labels: {
          boxWidth: 12,
        },
      },
      tooltips: {
        callbacks: {
          // eslint-disable-next-line no-shadow
          label(tooltipItem: TooltipItemType, data: DataType) {
            return data.labels[tooltipItem.index];
          },
        },
      },
      maintainAspectRatio: false,
    }),
    [isSP],
  );

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <PieComponent options={options} data={data} />
    </Suspense>
  );
};
